// Iter 88o — DID Feed status pill for DID Master.
// Shows current provider, last-sync timestamp, and stale-DID count. On
// super_admin roles, exposes a "Sync now" button that fires the manual
// endpoint and refreshes the count.
//
// Kept as a self-contained sub-component so DidMaster.jsx stays focused
// on inventory table concerns.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Loader2, Database, PlugZap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PROVIDER_LABEL = {
  off:         "Off",
  mongo:       "Mongo collection",
  carrier_api: "Carrier API",
};

const PROVIDER_ICON = {
  off:         Database,
  mongo:       Database,
  carrier_api: PlugZap,
};

function fmtWhen(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1)   return "just now";
    if (diffMin < 60)  return `${diffMin}m ago`;
    if (diffMin < 24 * 60) return `${Math.floor(diffMin / 60)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

export function DidFeedStatus({ isSuperAdmin, onAfterSync }) {
  const [status,  setStatus]  = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/did-feed/status");
      setStatus(data);
    } catch { /* silent — page still works */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setBusy(true);
    const t = toast.loading("Syncing DID feed…");
    try {
      const { data } = await api.post("/did-feed/sync");
      if (data.error) {
        toast.warning(data.error, { id: t, duration: 6000 });
      } else {
        toast.success(
          `Synced ${data.upstream || 0} upstream · ${data.inserted || 0} new · ${data.marked_stale || 0} newly stale`,
          { id: t, duration: 6000 });
      }
      await load();
      if (onAfterSync) await onAfterSync();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed", { id: t });
    } finally { setBusy(false); }
  };

  if (loading || !status) return null;

  const provider = status.provider || "off";
  const isOff    = provider === "off";
  const Icon     = PROVIDER_ICON[provider] || Database;
  const lastRun  = status.last_run;

  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${
      isOff ? "border-slate-200 bg-slate-50 text-slate-600"
            : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
         data-testid="did-feed-status">
      <Icon className="h-3.5 w-3.5" />
      <span className="font-medium">
        Live feed: <span data-testid="did-feed-provider">{PROVIDER_LABEL[provider] || provider}</span>
      </span>
      {isOff ? (
        <span className="text-slate-500">&nbsp;&mdash; nightly sync is disabled</span>
      ) : (
        <>
          <span className="text-slate-500">
            &middot; last synced <span data-testid="did-feed-last-run">{fmtWhen(lastRun?.finished_at)}</span>
            {lastRun && !lastRun.error && (
              <> ({lastRun.upserted || 0} upserted, {lastRun.marked_stale || 0} marked stale)</>
            )}
          </span>
          {lastRun?.error && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                  title={lastRun.error}
                  data-testid="did-feed-error">
              <AlertTriangle className="h-3 w-3" /> last run had errors
            </span>
          )}
        </>
      )}
      {status.stale_count > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
              data-testid="did-feed-stale-count">
          <AlertTriangle className="h-3 w-3" /> {status.stale_count} stale DID{status.stale_count === 1 ? "" : "s"}
        </span>
      )}
      {isSuperAdmin && !isOff && (
        <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 gap-1"
                onClick={syncNow} disabled={busy}
                data-testid="did-feed-sync-now">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" />
                : <RefreshCw className="h-3 w-3" />}
          Sync now
        </Button>
      )}
    </div>
  );
}
